import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Mail, Check, Loader2 } from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import TwoFactorVerificationModal from './TwoFactorVerificationModal';
import { useAuthBranding } from '../hooks/useAuthBranding';

interface LoginProps {
  onToggleMode: () => void;
}

const Login: React.FC<LoginProps> = ({ onToggleMode }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFAUserId, setTwoFAUserId] = useState<number | null>(null);
  const [twoFAMethod, setTwoFAMethod] = useState<'app' | 'sms' | 'both'>('app');
  const [twoFAPhone, setTwoFAPhone] = useState<string | undefined>();

  const { login, requestMagicLink } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

const { branding: loginBranding, brandingLoaded } = useAuthBranding('Sign In');

  // Save access code to localStorage if present in URL
  useEffect(() => {
    const accessCode = searchParams.get('access_code');
    if (accessCode) {
      localStorage.setItem('pending_access_code', accessCode.toUpperCase());
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await login(email, password);

      // Check if 2FA is required
      if (response && 'requiresTwoFactor' in response && response.requiresTwoFactor) {
        setRequires2FA(true);
        setTwoFAUserId(response.userId);
        setTwoFAMethod(response.method);
        setTwoFAPhone(response.phoneNumber);
        setLoading(false);
        return;
      }

      // Save pending access code to user if present
      const pendingCode = localStorage.getItem('pending_access_code');
      if (pendingCode) {
        try {
          await fetch('/api/tier-access/save-pending', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
            },
            body: JSON.stringify({ code: pendingCode }),
          });
          localStorage.removeItem('pending_access_code');
        } catch (err) {
          console.error('Failed to save access code:', err);
        }
      }

// Normal login success — redirect to agency selector if needed
if ((response as any)?._needsAgencySelection) {
        navigate('/select-agency');
      } else {
        const returnTo = searchParams.get('returnTo');
        navigate(returnTo || '/dashboard');
      }
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

const handle2FASuccess = async (code: string, method: string, rememberDevice: boolean) => {
    setLoading(true);
    setError(null);

    try {
const response = await login(email, password, code, method, rememberDevice);
      if (response && 'user' in response) {
        setRequires2FA(false);
if ((response as any)?._needsAgencySelection) {
          navigate('/select-agency');
        } else {
          const returnTo = searchParams.get('returnTo');
          navigate(returnTo || '/dashboard');
        }
      } else if (response && 'requiresTwoFactor' in response) {
        setError('Invalid verification code');
        setLoading(false);
      } else {
        setError('Login failed after 2FA verification');
        setLoading(false);
      }
    } catch (err: any) {
      console.error('2FA login error:', err);
      setError(err.message || 'Login failed after 2FA');
      setLoading(false);
    }
  };

  const handleMagicLink = async () => {
    if (!email) {
      setError('Please enter your email address first');
      return;
    }

    setMagicLinkLoading(true);
    setError(null);
    setMagicLinkSent(false);

    try {
      await requestMagicLink(email);
      setMagicLinkSent(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send magic link');
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/auth/forgot-password');
  };

  const isFormValid = email.includes('@') && password.length > 0;

  // Styles
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 16px',
    backgroundColor: 'var(--v2-surface)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.9375rem',
    color: 'var(--v2-text)',
    outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    boxSizing: 'border-box',
    fontFamily: 'var(--font-sans)',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--v2-text)',
    marginBottom: '6px',
  };

  return (
    <>
      <div style={{
        minHeight: '100vh',
        backgroundColor: 'var(--v2-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        fontFamily: 'var(--font-sans)',
      }}>
        <div style={{
          backgroundColor: 'var(--v2-surface)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-xl)',
          border: '1px solid var(--v2-border)',
          maxWidth: '420px',
          width: '100%',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            padding: '32px 32px 24px',
            textAlign: 'center',
            borderBottom: '1px solid var(--v2-border)',
          }}>
{/* Logo/Brand */}
            {!brandingLoaded ? (
              <div style={{ height: '56px', margin: '0 auto 16px' }} />
            ) : (loginBranding?.login_logo_url || !loginBranding?.company_name || loginBranding?.company_name === 'DashboardFox') ? (
              <img 
src={loginBranding?.login_logo_url || '/assets/dashboardfox-logo.png'}
                alt={loginBranding?.company_name || 'DashboardFox'}
                style={{ height: '56px', maxWidth: '200px', objectFit: 'contain', margin: '0 auto 16px' }}
              />
            ) : (
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: 'var(--radius-lg)',
                background: 'linear-gradient(135deg, var(--v2-primary) 0%, #818cf8 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
              </div>
            )}
            <h1 style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: 'var(--v2-text)',
              margin: '0 0 4px 0',
            }}>
              Welcome back
            </h1>
            <p style={{
              fontSize: '0.9375rem',
              color: 'var(--v2-text-tertiary)',
              margin: 0,
            }}>
Sign in to your {loginBranding?.company_name || 'DashboardFox'} account              
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div style={{ padding: '24px 32px' }}>
              {/* Error Alert */}
              {error && (
                <div style={{
                  backgroundColor: 'var(--v2-error-bg)',
                  border: '1px solid #fecaca',
                  borderRadius: 'var(--radius-md)',
                  padding: '12px 16px',
                  marginBottom: '20px',
                }}>
                  <p style={{ fontSize: '0.875rem', color: 'var(--v2-error)', margin: 0 }}>{error}</p>
                </div>
              )}

              {/* Magic Link Success */}
              {magicLinkSent && (
                <div style={{
                  backgroundColor: 'var(--v2-success-bg)',
                  border: '1px solid #86efac',
                  borderRadius: 'var(--radius-md)',
                  padding: '12px 16px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                }}>
                  <Check size={16} strokeWidth={2} color="var(--v2-success)" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <p style={{ fontSize: '0.875rem', color: 'var(--v2-success-dark)', margin: 0 }}>
                    Magic link sent! Check your email and click the link to sign in.
                  </p>
                </div>
              )}

              {/* Email */}
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  disabled={loading || magicLinkLoading}
                  autoFocus
                  autoComplete="email"
                  style={{
                    ...inputStyle,
                    opacity: (loading || magicLinkLoading) ? 0.6 : 1,
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--v2-primary)';
                    e.target.style.boxShadow = '0 0 0 3px var(--v2-primary-muted)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--v2-border)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Password */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>Password</label>
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--v2-primary)',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    Forgot password?
                  </button>
                </div>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    disabled={loading || magicLinkLoading}
                    autoComplete="current-password"
                    style={{
                      ...inputStyle,
                      paddingRight: '48px',
                      opacity: (loading || magicLinkLoading) ? 0.6 : 1,
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'var(--v2-primary)';
                      e.target.style.boxShadow = '0 0 0 3px var(--v2-primary-muted)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'var(--v2-border)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '14px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--v2-text-tertiary)',
                      padding: '4px',
                      display: 'flex',
                    }}
                  >
                    {showPassword ? <EyeOff size={18} strokeWidth={1.5} /> : <Eye size={18} strokeWidth={1.5} />}
                  </button>
                </div>
              </div>

              {/* Sign In Button */}
              <button
                type="submit"
                disabled={loading || magicLinkLoading || !isFormValid}
                style={{
                  width: '100%',
                  padding: '14px 24px',
                  backgroundColor: (loading || magicLinkLoading || !isFormValid) ? 'var(--v2-border)' : 'var(--v2-primary)',
                  color: (loading || magicLinkLoading || !isFormValid) ? 'var(--v2-text-muted)' : '#ffffff',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '0.9375rem',
                  fontWeight: 600,
                  cursor: (loading || magicLinkLoading || !isFormValid) ? 'not-allowed' : 'pointer',
                  marginTop: '20px',
                  transition: 'background-color 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                {loading && <Loader2 size={18} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />}
                {loading ? 'Signing in...' : 'Sign In'}
              </button>

              {/* Divider */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                margin: '24px 0',
                gap: '12px',
              }}>
                <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--v2-border)' }} />
                <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-muted)', fontWeight: 500 }}>or</span>
                <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--v2-border)' }} />
              </div>

              {/* Magic Link Button */}
              <button
                type="button"
                onClick={handleMagicLink}
                disabled={!email || magicLinkLoading || loading}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  backgroundColor: 'var(--v2-surface)',
                  color: (!email || magicLinkLoading || loading) ? 'var(--v2-text-muted)' : 'var(--v2-text)',
                  border: '1px solid var(--v2-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: (!email || magicLinkLoading || loading) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: (!email || magicLinkLoading || loading) ? 0.6 : 1,
                  transition: 'all 0.15s',
                }}
              >
                {magicLinkLoading ? (
                  <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Mail size={16} strokeWidth={1.5} />
                )}
                {magicLinkLoading ? 'Sending...' : 'Sign in with Magic Link'}
              </button>

              <p style={{
                fontSize: '0.8125rem',
                color: 'var(--v2-text-muted)',
                textAlign: 'center',
                marginTop: '12px',
                marginBottom: 0,
              }}>
                We'll email you a secure link to sign in without a password
              </p>
            </div>

            {/* Footer */}
            <div style={{
              padding: '20px 32px',
              borderTop: '1px solid var(--v2-border)',
              backgroundColor: 'var(--v2-surface-alt)',
              textAlign: 'center',
            }}>
              <p style={{
                fontSize: '0.875rem',
                color: 'var(--v2-text-tertiary)',
                margin: 0,
              }}>
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={onToggleMode}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--v2-primary)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: '0.875rem',
                  }}
                >
                  Create one now
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* 2FA Verification Modal */}
      {requires2FA && twoFAUserId && (
        <TwoFactorVerificationModal
          open={requires2FA}
          userId={twoFAUserId}
          availableMethods={twoFAMethod}
          phoneNumber={twoFAPhone}
          onSuccess={handle2FASuccess}
          onCancel={() => {
            setRequires2FA(false);
            setError(null);
            setLoading(false);
          }}
        />
      )}
    </>
  );
};

export default Login;
